/**
 * VideoConfigTabContent — Config tab content for the Video Generator bottom panel.
 *
 * Contains: reference image picker slot, motion prompt slot, control bar
 * (config pill, retry selector, sparks count, generate button), and the config flyout.
 *
 * The config flyout is rendered via FlyoutPortal to escape overflow-hidden clipping.
 */

import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { VideoConfigFlyout } from './VideoConfigFlyout'
import { FlyoutPortal } from './FlyoutPortal'
import { useClickOutside } from './useClickOutside'
import { videoSparksPerGeneration } from '@ponpon/api-client'

/** Sparks shown on the regen pill = per-second rate × duration × retry count.
 * Backed by VIDEO_MODEL_USD_PER_SECOND (generated from google_pricing.json) so
 * the pill matches what _deduct_sparks will actually bill on submit. */
function getVideoSparks(
  model: string,
  resolution: string,
  durationSeconds: number,
  retryCount: number,
): number {
  return videoSparksPerGeneration(model, resolution, durationSeconds) * Math.max(1, retryCount)
}

export interface VideoConfigTabContentProps {
  model: string
  onModelChange: (model: string) => void
  referenceImagePicker?: React.ReactNode
  motionPromptContent?: React.ReactNode
  duration: number
  resolution: string
  aspectRatio: string
  generateAudio: boolean
  ttsProvider: string
  /** Controls how dialogue is delivered: 'tts_remix' (default) or 'seedance_inline'. */
  dialogueMode?: string
  onDialogueModeChange?: (mode: string) => void
  onDurationChange: (duration: number) => void
  onResolutionChange: (resolution: string) => void
  onAspectRatioChange: (aspectRatio: string) => void
  onGenerateAudioChange: (generateAudio: boolean) => void
  onTtsProviderChange: (provider: string) => void
  isRunning: boolean
  onGenerate: (retryCount: number) => void
  onCancel?: () => void
  isCancelling?: boolean
  activeTab: string
  /**
   * When false, the Generate button is disabled (e.g. because the structured
   * motion prompt has overlapping video segments). Defaults to true so
   * existing consumers (ShortVideoNode) are unaffected.
   */
  canGenerate?: boolean
  /** Tooltip shown on the disabled Generate button. */
  cannotGenerateReason?: string
}

export const VideoConfigTabContent = memo(function VideoConfigTabContent({
  model,
  onModelChange,
  referenceImagePicker,
  motionPromptContent,
  duration,
  resolution,
  aspectRatio,
  generateAudio,
  ttsProvider,
  dialogueMode = 'tts_remix',
  onDialogueModeChange,
  onDurationChange,
  onResolutionChange,
  onAspectRatioChange,
  onGenerateAudioChange,
  onTtsProviderChange,
  isRunning,
  onGenerate,
  onCancel,
  isCancelling,
  activeTab,
  canGenerate = true,
  cannotGenerateReason,
}: VideoConfigTabContentProps) {
  const [retryCount, setRetryCount] = useState(1)
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  const pillRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)

  const closeFlyout = useCallback(() => setIsFlyoutOpen(false), [])
  const closeRetryPopover = useCallback(() => setIsRetryPopoverOpen(false), [])

  // Computed values
  const sparksCount = getVideoSparks(model, resolution, duration, retryCount)
  const audioIcon = generateAudio ? '\u{1F50A}' : '\u{1F507}'
  // Model + TTS provider are server-owned; hide both from the user-facing pill.
  const configPillLabel = `${aspectRatio} · ${resolution === 'auto' ? 'Auto' : resolution === '4k' ? '4K' : resolution} · ${duration}s · ${audioIcon}`

  // Close flyout when switching away from config tab
  useEffect(() => {
    if (activeTab !== 'config') {
      setIsFlyoutOpen(false)
    }
  }, [activeTab])

  // Click-outside for retry popover
  useClickOutside([retryPopoverRef, retryPillRef], closeRetryPopover, isRetryPopoverOpen)

  // Generate handler
  const handleGenerate = useCallback(() => {
    onGenerate(retryCount)
  }, [onGenerate, retryCount])

  return (
    <div className="flex flex-col nowheel min-h-0">
      {/* Scrollable config content */}
      <div className="flex-1 p-3 space-y-3 overflow-y-auto">
        {referenceImagePicker}
        {motionPromptContent}
      </div>

      {/* Control Bar -- fixed at bottom */}
      <div className="flex items-center gap-2 px-3 py-1 border-t border-border-subtle" data-testid="video-control-bar">
        {/* Config Pill */}
        <button
          ref={pillRef}
          onClick={() => setIsFlyoutOpen(!isFlyoutOpen)}
          className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
          data-testid="video-config-pill"
        >
          {configPillLabel}
        </button>

        {/* Config Flyout — portal-based */}
        <FlyoutPortal
          triggerRef={pillRef}
          isOpen={isFlyoutOpen}
          onClose={closeFlyout}
        >
          <VideoConfigFlyout
            model={model}
            onModelChange={onModelChange}
            aspectRatio={aspectRatio}
            onAspectRatioChange={onAspectRatioChange}
            resolution={resolution}
            onResolutionChange={onResolutionChange}
            duration={duration}
            onDurationChange={onDurationChange}
            generateAudio={generateAudio}
            onGenerateAudioChange={onGenerateAudioChange}
            ttsProvider={ttsProvider}
            onTtsProviderChange={onTtsProviderChange}
            dialogueMode={dialogueMode}
            onDialogueModeChange={onDialogueModeChange}
          />
        </FlyoutPortal>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Retry Count Pill + Popover */}
        <div className="relative" data-testid="video-retry-selector">
          <button
            ref={retryPillRef}
            onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
            className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
            data-testid="video-retry-pill"
          >
            {retryCount}x
          </button>

          {isRetryPopoverOpen && (
            <div
              ref={retryPopoverRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="video-retry-popover"
            >
              {([4, 3, 2, 1]).map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setRetryCount(n)
                    setIsRetryPopoverOpen(false)
                  }}
                  className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                    retryCount === n
                      ? 'text-primary-foreground'
                      : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                  }`}
                  style={
                    retryCount === n
                      ? { backgroundColor: 'var(--primary)' }
                      : undefined
                  }
                  data-testid={`video-retry-${n}x`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sparks Count */}
        <span className="flex items-center gap-1 text-xs text-text-muted tabular-nums" data-testid="video-sparks-count">
          <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
          </svg>
          {sparksCount}
        </span>

        {/* Generate / Stop Button */}
        {isRunning && onCancel ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
            disabled={isCancelling}
            className="w-6 h-6 flex items-center justify-center rounded-full border border-destructive/50 bg-destructive/15 text-destructive hover:bg-destructive/25 hover:border-destructive transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={isCancelling ? 'Stopping...' : 'Stop'}
            data-testid="cancel-node-button"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="1" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={isRunning || canGenerate === false}
            title={canGenerate === false ? cannotGenerateReason : 'Generate'}
            className="w-6 h-6 flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="video-generate-button"
          >
            <svg className="w-3.5 h-3.5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
})

VideoConfigTabContent.displayName = 'VideoConfigTabContent'
